<template>
  <Teleport to="body">
    <div
      v-if="isVisible && !isMobile"
      class="modal-overlay"
      @click="closeModal"
    >
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2 class="modal-title">
            <svg
              class="help-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
            How to Use the Competency Board
          </h2>
          <button
            class="close-button"
            @click="closeModal"
            aria-label="Close modal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="instruction-section">
            <h3>Competency Categories</h3>
            <div style="padding-left: 1.5rem">
              <p class="category-note">
                Each competency category is color coded
              </p>
              <p
                class="category-explanation technical"
                style="border-left-color: var(--primary-color, #40916c)"
              >
                <strong style="color: var(--primary-color, #40916c)"
                  >Green</strong
                ><br />
                Technical competencies (hands-on technical skills)<br />
              </p>
              <p
                class="category-explanation non-technical"
                style="border-left-color: var(--secondary-color, #6366f1)"
              >
                <strong style="color: var(--secondary-color, #6366f1)"
                  >Orange</strong
                ><br />
                Non-Technical competencies (soft skills and knowledge)
              </p>
            </div>
          </div>

          <div class="instruction-section">
            <h3>How to Use</h3>
            <ol class="action-list">
              <li>
                <strong>Drag & Drop:</strong> Click and drag competencies or entire catagories into a column. 
                If you drag a full category into a column by accident, you can press X to make it return to the left panel.
              </li>
              <li>
                <strong>Column Organization:</strong> Drop items into columns
                that represent your proficiency level or preference.
              </li>
              <li>
                <strong>Visual Feedback:</strong> The colored dots help you
                identify technical vs. non-technical competencies at a glance.
              </li>
              <li>
                <strong>Information:</strong> Click the info button ⓘ on any
                competency to learn more about it.
              </li>
              <li>
                <strong>Note:</strong> All competencies not dragged into a column will be marked as Not Applicable
              </li>
            </ol>
          </div>

          <div class="instruction-section">
            <h3>💡 Tips</h3>
            <ul class="tips-list">
              <li>
                Take your time to read competency descriptions before making
                decisions
              </li>
              <li>Consider both your current skills</li>
              <li>
                You can always drag items between columns if you change your
                mind
              </li>
              <li>The board automatically saves your progress as you work</li>
            </ul>
          </div>
        </div>
        <div class="modal-footer">
          <button class="primary-button" @click="closeModal">Got it!</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useWindowSize } from "@vueuse/core";

interface Props {
  isVisible: boolean;
}

interface Emits {
  (e: "close"): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { width } = useWindowSize();

const isMobile = computed(() => width.value < 768);

const closeModal = () => {
  emit("close");
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 0.75rem;
  max-width: 850px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: modalSlideIn 0.3s ease-out;
  overflow: hidden; /* Prevent content from overflowing rounded corners */
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.5rem 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: white;
  border-radius: 0.75rem 0.75rem 0 0;
  flex-shrink: 0;
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  flex: 1;
}

.help-icon {
  color: #3b82f6;
}

.close-button {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: #6b7280;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: end;
  flex-shrink: 0;
  margin-left: 1rem;
}

.close-button:hover {
  color: #374151;
  background-color: #f3f4f6;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.instruction-section {
  margin-bottom: 2rem;
}

.instruction-section:last-of-type {
  margin-bottom: 0;
}

.instruction-section h3 {
  margin: 0 0 1rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.instruction-section p {
  margin: 0 0 1rem 0;
  color: #4b5563;
  line-height: 1.6;
}

.category-note {
  margin: 0 0 1rem 0;
  color: #6b7280;
  font-size: 0.875rem;
  font-style: italic;
}

.category-explanation {
  margin: 1rem 0 0 0;
  color: #4b5563;
  font-size: 0.875rem;
  line-height: 1.5;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 0.5rem;
  border-left: 4px solid #e5e7eb;
}

.category-examples {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.category-item {
  background: #ebecf0;
  border-radius: 0.375rem;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.category-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.category-item.technical {
  background: var(--primary-color, #40916c);
  color: white;
  border: 2px solid var(--primary-color, #40916c);
}

.category-item.non-technical {
  background: var(--secondary-color, #6366f1);
  color: white;
  border: 2px solid var(--secondary-color, #6366f1);
}

.group-header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0.75rem 0.5rem 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 0.375rem 0.375rem 0 0;
}

.group-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.group-title-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.drag-handle {
  font-size: 0.875rem;
  color: #9ca3af;
  cursor: grab;
  user-select: none;
}

.group-title {
  font-weight: 500;
  color: #1f2937;
  font-size: 0.9375rem;
}

.expand-icon {
  font-size: 1rem;
  color: #9ca3af;
  cursor: pointer;
  user-select: none;
}

.group-competencies {
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.competency-item {
  background: #f9fafb;
  border-radius: 0.375rem;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.competency-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.competency-item.technical {
  background: var(--primary-color, #40916c);
  color: white;
  border: 2px solid var(--primary-color, #40916c);
}

.competency-item.non-technical {
  background: var(--secondary-color, #6366f1);
  color: white;
  border: 2px solid var(--secondary-color, #6366f1);
}

.competency-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem;
}

.competency-title {
  flex: 1;
  font-weight: 500;
  font-size: 0.9375rem;
}

.tooltip-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  z-index: 9999;
}

.info-button {
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: inherit;
  transition: all 0.2s ease;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.info-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.type-indicators {
  display: flex;
  gap: 0.25rem;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.type-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

.type-dot.technical {
  background-color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.9);
}

.type-dot.non-technical {
  background-color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.9);
}

.action-list,
.tips-list {
  margin: 0;
  padding-left: 1.5rem;
  color: #4b5563;
  line-height: 1.6;
}

.action-list li,
.tips-list li {
  margin-bottom: 0.5rem;
}

.action-list li:last-child,
.tips-list li:last-child {
  margin-bottom: 0;
}

.proficiency-examples {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.proficiency-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
}

.level-name {
  font-weight: 600;
  color: #1f2937;
}

.level-desc {
  font-size: 0.875rem;
  color: #6b7280;
}

.modal-footer {
  padding: 1rem 1.5rem 1.5rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  background: white;
  border-radius: 0 0 0.75rem 0.75rem;
  flex-shrink: 0;
}

.primary-button {
  background-color: var(--primary-color);
  color: white;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  width: 100%;
}

.primary-button:hover:not(:disabled) {
  background-color: var(--primary-hover);
}

.primary-button:active {
  transform: scale(0.98);
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 0.5rem;
  }

  .modal-content {
    max-width: calc(100vw - 1rem);
    max-height: calc(100vh - 1rem);
    border-radius: 0.5rem;
  }

  .modal-header {
    padding: 1rem;
    border-radius: 0.5rem 0.5rem 0 0;
  }

  .modal-title {
    font-size: 1.25rem;
  }

  .modal-body {
    padding: 1rem;
  }

  .modal-footer {
    padding: 1rem;
    border-radius: 0 0 0.5rem 0.5rem;
  }

  .category-content {
    padding: 0.5rem;
  }

  .type-indicators {
    padding: 0.5rem;
  }

  .proficiency-item {
    padding: 0.5rem;
  }
}

@media (max-width: 480px) {
  .modal-overlay {
    padding: 0.25rem;
  }

  .modal-content {
    max-width: calc(100vw - 0.5rem);
    max-height: calc(100vh - 0.5rem);
  }

  .modal-header {
    padding: 0.75rem;
  }

  .modal-title {
    font-size: 1.125rem;
  }

  .modal-body {
    padding: 0.75rem;
  }

  .modal-footer {
    padding: 0.75rem;
  }

  .instruction-section h3 {
    font-size: 1rem;
  }

  .action-list,
  .tips-list {
    padding-left: 1.25rem;
  }

  .category-examples {
    gap: 0.5rem;
  }

  .proficiency-examples {
    gap: 0.5rem;
  }
}

/* Custom scrollbar for modal body */
.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: transparent;
}

.modal-body::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.3);
}

/* Firefox scrollbar */
.modal-body {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}
</style>
